import { mdiFeather } from '@mdi/js'
import { HelpToggleButton } from 'components/App/components/HelpToggleButton'
import ConfirmDialog from 'components/ConfirmDialog'
import { NOTIFICATION_DURATION_LONG } from 'components/ConnectedSnackbar'
import EmptyView from 'components/EmptyView'
import { FailedPost } from 'components/Posts'
import PostFilesDialog from 'components/Posts/PostFilesDialog'
import ScrollListener from 'components/ScrollListener'
import SimpleLoader from 'components/SimpleLoader'
import { FailedPost as IFailedPost } from 'interfaces'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, useLocation, useNavigate } from 'react-router'
import { BulkPostActions } from 'routes/publishing/components/BulkActionsMenus'
import { ERROR_NO_PROFILES_SUBTITLE, ERROR_NO_PROFILES_TITLE, PostErrorView } from 'routes/publishing/components/PostErrorView'
import { of as observableOf, Subject } from 'rxjs'
import { catchError, mergeMap, switchMap, tap } from 'rxjs/operators'
import { getDerivedStateFromPost, mergeState, resetComposer } from 'services/compose'
import { connectedDestinationsSelector } from 'services/destinations'
import { bulkDeletePosts, getFailedPosts, getFailedPostsCount, requeuePost, setSelectedProfiles } from 'services/post/actions'
import { failedPostsStatusSelector, selectedProfilesFetchIdsSelector, selectedProfilesPPIdsSelector } from 'services/post/selectors'
import { publishPosts } from 'services/post/shareNow/actions'
import { message } from 'services/snackbar'
import { POST_TYPES } from 'shared/constants'
import { ContentType } from 'shared/types'
import { StoreThunkDispatch } from 'store/state'
import { addPosts, deleteMultiplePosts, setPosts } from './actions'
import PostsFilter from './components/PostsFilter'
import styles from './PostViews.pcss'
import { PostsAction, postsReducer } from './reducer'

const SCROLL_TRESHOLD = 350
interface FetchFailedParams {
  page: number;
  pageIds: string[];
  ppids?: string[];
  query?: string;
  types?: ContentType[];
  searchInArticleSummary?: boolean;
  bucketId?: string;
}

export function FailedPostsManager({ bucketId, fetchIds }: { bucketId?: string, fetchIds?: string }) {
  const intl = useIntl()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const selectedProfilesPpids = useSelector(selectedProfilesPPIdsSelector)
  const connectedProfiles = useSelector(connectedDestinationsSelector)
  // NOTE: When the component is rendered from Bucket posts view, we pass the currently selected profile fetch id in the bucket.
  // We want to use that instead of the selected profiles in the global state.
  const globalSelectedFetchIds = useSelector(selectedProfilesFetchIdsSelector)
  const selectedProfilesFetchIds = fetchIds || globalSelectedFetchIds
  const failedPostsStatus = useSelector(failedPostsStatusSelector)
  const [posts, postsDispatch] = useReducer<IFailedPost[], [PostsAction]>(postsReducer, [])
  const postsRef = useRef<IFailedPost[]>(posts)
  const [totalPostsCount, setTotalPostsCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingOverlayActive, setLoadingOverlayActive] = useState(false)
  const [page, setPage] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(true)
  const [refreshKey, setRefreshKey] = useState(0)
  const [postInMediaPreviewDialog, setPostInMediaPreviewDialog] = useState<IFailedPost | null>(null)
  const [selectedPostsIds, setSelectedPostsIds] = useState<Record<string, boolean> | 'all'>({})
  const [search, setSearch] = useState<{ query: string, types: ContentType[], searchInArticleSummary?: boolean }>(
    { query: '', types: POST_TYPES as ContentType[], searchInArticleSummary: true }
  )

  const share$ = useRef<Subject<{ postIds: string[], ppids: string[], all: boolean }>>(undefined)
  const fetch$ = useRef<Subject<FetchFailedParams>>(undefined)
  const delete$ = useRef<Subject<{
    postIds: string[], ppids: string[], all: boolean, query: string, types: ContentType[]
   }>>(undefined)
  const shareNext$ = useRef<Subject<{ postIds: string[], ppids: string[], all: boolean, deletePosts?: boolean }>>(undefined)
  const scrollElementRef = useRef<HTMLElement | undefined>(undefined)
  const selectionRef = useRef<{ postIds: string[], profileIds: string[], all: boolean, ppids: string[] }>(undefined)

  useLayoutEffect(() => {
    scrollElementRef.current = document.querySelector('[data-test="main"]') as HTMLElement
  }, [])

  useEffect(() => {
    // EXPL: keep latest posts in a ref so item callbacks stay referentially stable
    // across posts updates and don't force every list item to re-render
    postsRef.current = posts
  }, [posts])

  const onFilterPosts = useCallback((query: string, types: ContentType[], searchInArticleSummary?: boolean) => {
    setSearch({ query, types, searchInArticleSummary })
  }, [])

  useEffect(() => {
    fetch$.current = new Subject()
    const subscription = fetch$.current
      .pipe(
        tap(({ page }) => {
          setLoading(true)
          setPage(page)
        }),
        switchMap(({ pageIds, page, ppids, query, searchInArticleSummary, types }) => {
          const fetchAction = dispatch(getFailedPosts(page, pageIds, ppids, query, types, searchInArticleSummary, bucketId))
          // Ensure the action is an Observable
          if (!fetchAction.pipe) {
            return observableOf({ error: true })
          }
          return fetchAction.pipe(
            catchError((e) => {
              console.log(e)
              dispatch(message('Error fetching posts! Please refresh.', 'error'))
              return observableOf({ error: true })
            })
          )
        })
      )
      .subscribe({
        next: (response: any) => {
          setLoading(false)
          setLoadingOverlayActive(false)
          setTotalPostsCount(response.totalPostsCount)
          if (!response.error) {
            setHasNextPage(response.totalPostsCount > response.posts.length)
            if (response.page === 0) {
              postsDispatch(setPosts(response.posts))
            } else {
              postsDispatch(addPosts(response.posts))
            }
          }
        },
        error: (error) => {
          setLoading(false)
          setLoadingOverlayActive(false)
          console.error('Failed posts fetch error:', error)
          dispatch(message('Error fetching posts! Please refresh.', 'error'))
        }
      })

    return () => {
      subscription.unsubscribe()
      fetch$.current?.complete()
      fetch$.current?.unsubscribe()
    }
  }, [dispatch, selectedProfilesPpids, bucketId])

  useEffect(() => {
    delete$.current = new Subject()
    delete$.current
      .pipe(
        tap(() => {
          setLoadingOverlayActive(true)
        }),
        mergeMap(({ ppids, postIds, all }) => {
          return dispatch(bulkDeletePosts(postIds, ppids, 'failed', all, bucketId))
            .pipe(catchError((error: Error) => {
              dispatch(message(intl.formatMessage({ id: 'post.notifications.delete-posts-error' }, { error: error.message }), 'error'))
              return observableOf({ error })
            }))
        })
      )
      .subscribe((response: any) => {
        if (!response.error) {
          setSelectedPostsIds({})
          if (response.all) {
            return
          }
          setRefreshKey(current => current + 1)
          postsDispatch(deleteMultiplePosts(response.ids))
          dispatch(getFailedPostsCount(selectedProfilesPpids))
        }
        setLoadingOverlayActive(false)
      })

    return () => delete$.current?.unsubscribe()
  }, [bucketId, dispatch, intl, selectedProfilesPpids])

  useEffect(() => {
    shareNext$.current = new Subject()
    shareNext$.current
      .pipe(
        tap(() => {
          setLoadingOverlayActive(true)
        }),
        mergeMap(({ ppids, postIds, all, deletePosts }) => {
          return dispatch(requeuePost(postIds, ppids, 'failed', all))
            .pipe(catchError((error: Error) => {
              if (error.message) {
                dispatch(message(error.message, 'error', NOTIFICATION_DURATION_LONG))
              } else {
                dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
              }
              return observableOf({ error })
            }), tap(() => {
              if (deletePosts) {
                delete$.current?.next({ postIds, ppids, all, query: search.query, types: search.types })
              }
            }))
        })
      )
      .subscribe((response: any) => {
        if (!response.error) {
          setSelectedPostsIds({})
          dispatch(message(intl.formatMessage({ id: 'post.notifications.posts-requeued' }), 'success'))
        }
        setLoadingOverlayActive(false)
      })

    return () => shareNext$.current?.unsubscribe()
  }, [dispatch, intl, search.query, search.types])

  useEffect(() => {
    share$.current = new Subject()
    share$.current
      .pipe(
        tap(() => {
          setLoadingOverlayActive(true)
        }),
        mergeMap(({ ppids, postIds, all }) => {
          return dispatch(publishPosts(postIds, ppids, 'failed', all))
            .pipe(catchError((error: Error) => {
              dispatch(message(intl.formatMessage({ id: 'notifications.error-w-message' }, { error: error.message }), 'error'))
              return observableOf({ error })
            }))
        })
      )
      .subscribe((response: any) => {
        setLoadingOverlayActive(false)
        if (!response.error) {
          setRefreshKey(current => current + 1)
          setSelectedPostsIds({})
          dispatch(message(intl.formatMessage({ id: 'notifications.post-posted' }), 'success'))
        }
      })

    return () => share$.current?.unsubscribe()
  }, [dispatch, intl])

  useEffect(() => {
    postsDispatch(setPosts([]))
  }, [selectedProfilesFetchIds])

  useEffect(() => {
    if (selectedProfilesFetchIds.length > 0) {
      const pageIds = selectedProfilesFetchIds.split(',')
      fetch$.current?.next({
        page: 0,
        pageIds,
        ppids: Object.values(connectedProfiles).filter(p => pageIds.includes(p.idNew)).map(p => p.ppid),
        ...search
      })
    }
  }, [selectedProfilesFetchIds, refreshKey, connectedProfiles, search])

  useEffect(() => {
    const profileIds = selectedProfilesFetchIds.split(',')
    const ppids = Object.values(connectedProfiles).filter(p => profileIds.includes(p.idNew)).map(p => p.ppid)
    if (selectedPostsIds === 'all') {
      selectionRef.current = {
        all: true,
        postIds: posts.map(p => p.id),
        profileIds,
        ppids
      }
    } else {
      selectionRef.current = {
        all: false,
        profileIds,
        ppids,
        postIds: Object.keys(selectedPostsIds).filter(id => selectedPostsIds[id])
      }
    }
  }, [selectedPostsIds, posts, selectedProfilesFetchIds, connectedProfiles])

  const loadNextPage = useCallback(() => {
    if (hasNextPage && !loading) {
      const pageIds = selectedProfilesFetchIds.split(',')
      fetch$.current?.next({
        page: page + 1,
        pageIds,
        ppids: Object.values(connectedProfiles).filter(p => pageIds.includes(p.idNew)).map(p => p.ppid),
        ...search
      })
    }
  }, [page, selectedProfilesFetchIds, hasNextPage, loading, search, connectedProfiles])

  const closePostMediaPreviewDialog = useCallback(() => {
    setPostInMediaPreviewDialog(null)
  }, [])

  const onRepost = useCallback((post: IFailedPost) => {
    dispatch(resetComposer())
    dispatch(mergeState(getDerivedStateFromPost(post, 'failed')))
    navigate('/composer', { state: { floating: true, modal: 'compose', opener: '/history/failed', background: location } })
  }, [dispatch, navigate, location])

  const togglePostSelected = useCallback((id: string) => {
    setSelectedPostsIds(current => {
      if (current === 'all') {
        return postsRef.current.reduce((selection: Record<string, boolean>, post) => {
          selection[post.id] = post.id !== id
          return selection
        }, {})
      }
      return { ...current, [id]: !current[id] }
    })
  }, [])

  const toggleAllPostsSelected = useCallback(() => {
    setSelectedPostsIds(current => current === 'all' ? {} : 'all')
  }, [])

  const onDeletePost = useCallback((id: string) => {
    delete$.current?.next({
      postIds: [id],
      ppids: selectionRef.current?.ppids || [],
      all: false,
      query: search.query,
      types: search.types
    })
  }, [search.query, search.types])

  const deleteSelectedPosts = useCallback(() => {
    if (selectionRef.current) {
      delete$.current?.next({ ...selectionRef.current, query: search.query, types: search.types })
    }
  }, [search.query, search.types])

  const shareNextPosts = useCallback((deletePosts?: boolean) => {
    shareNext$.current?.next({
      postIds: selectionRef.current?.postIds || [],
      ppids: selectionRef.current?.ppids || [],
      all: Boolean(selectionRef.current?.all),
      deletePosts: Boolean(deletePosts)
    })
  }, [])

  const shareNextAndDeletePosts = useCallback(() => {
    shareNextPosts(true)
  }, [shareNextPosts])

  const onShareNow = useCallback((post: { id: string, ppPageId: string }) => {
    share$.current?.next({ all: false, postIds: [post.id], ppids: [post.ppPageId] })
  }, [])

  const onShareNext = useCallback((post: { id: string, ppPageId: string }, shouldDelete?: boolean) => {
    shareNext$.current?.next({
      postIds: [post.id],
      ppids: [post.ppPageId],
      all: false,
      deletePosts: Boolean(shouldDelete)
    })
  }, [])

  const deletePostsConfirmMessage = selectedPostsIds === 'all'
    ? intl.formatMessage({ id: 'post.delete-all-confirm' }, { count: totalPostsCount, type: 'failed' })
    : undefined

  const selectedPostsCount = useMemo(() => {
    return selectedPostsIds !== 'all'
      ? Object.values(selectedPostsIds).filter(Boolean).length
      : totalPostsCount
  }, [totalPostsCount, selectedPostsIds])

  const selectAllProfiles = () => {
    dispatch(setSelectedProfiles({ profiles: Object.values(connectedProfiles), activeView: 'history' }))
  }

  // EXPL: memoize the rendered items so manager-only state changes (loading, media
  // dialog, pagination, count) don't rebuild the whole list (no virtualization here)
  const renderedPosts = useMemo(() => posts.map((post: IFailedPost) => (
    <FailedPost
      key={post.id}
      selected={selectedPostsIds === 'all' || Boolean(selectedPostsIds[post.id])}
      post={post}
      onRepost={onRepost}
      onDelete={onDeletePost}
      onToggleSelected={togglePostSelected}
      onMediaPreviewBtnClick={setPostInMediaPreviewDialog}
      onShareNow={onShareNow}
      onShareNext={onShareNext}
    />
  )), [posts, selectedPostsIds, onRepost, onDeletePost, togglePostSelected, onShareNow, onShareNext])

  return (
    <div className={`${styles.wrapper} ${styles['wrapper-failed']}`}>
      <div className={styles.content}>
        {loadingOverlayActive && (
          <div className={styles['loading-overlay']}>
            <SimpleLoader small />
          </div>
        )}
        <div className={`${styles.header} ${styles['header-failed']}`}>
          <PostsFilter activeTypes={search.types} className={styles['failed-search']} onFilter={onFilterPosts} />
          {typeof totalPostsCount === 'number' && (
            <div className={styles.count}>
              <FormattedMessage id="post.planned.posts-count-label" values={{ count: totalPostsCount }} />
            </div>
          )}
          <ConfirmDialog
            message="What should happen to the selected failed posts after this action?"
            variant="danger"
            confirmLabel="Delete from Failed"
            secondaryLabel="Keep in Failed"
            onSecondary={shareNextPosts}
          >
            {(confirm: any) => (
              <BulkPostActions
                hidePostActions={!loading && posts.length === 0}
                withDelete
                disabled={selectedPostsCount === 0}
                allSelected={selectedPostsIds === 'all'}
                selectedCount={selectedPostsCount}
                deleteAllConfirmMessage={deletePostsConfirmMessage}
                onBulkShareNext={confirm(shareNextAndDeletePosts)}
                onToggleAllSelected={toggleAllPostsSelected}
                onBulkDeletePosts={deleteSelectedPosts}
              />
            )}
          </ConfirmDialog>
          <HelpToggleButton />
        </div>
        <div className={styles['failed-list-wrapper']} data-test="post-list">
          <ScrollListener
            scrollElement={scrollElementRef.current}
            emitTreshold={SCROLL_TRESHOLD}
            onScroll={loadNextPage}
          >
            <div>
              {renderedPosts}
            </div>
          </ScrollListener>
        </div>
        {!loading && selectedProfilesPpids.length === 0 && (
          <PostErrorView title={ERROR_NO_PROFILES_TITLE} subtitle={ERROR_NO_PROFILES_SUBTITLE} />
        )}
        {!loading && selectedProfilesPpids.length !== 0 && posts.length === 0 && (
          <EmptyView
            icon={mdiFeather}
            title="No failed posts"
            subtitle={(
              <div>
                <NavLink to={{ pathname: '/composer' }} state={{ floating: true, modal: 'compose', opener: location.pathname }}>
                  Create a new post
                </NavLink>
                &nbsp;or&nbsp;<NavLink to="/content">search for content</NavLink>
                {!failedPostsStatus.loading && failedPostsStatus.hasFailedPosts && (
                  <div className={styles['failed-empty-subtitle']}>
                    To see the failed posts for all pages, click <span onClick={selectAllProfiles}>here</span>
                  </div>
                )}
              </div>
            )}
            backdropClassName={styles['failed-empty-overlay']}
            backdrop
          />
        )}
        {loading && <SimpleLoader small />}
      </div>
      <PostFilesDialog
        active={Boolean(postInMediaPreviewDialog)}
        images={postInMediaPreviewDialog?.images || []}
        onClose={closePostMediaPreviewDialog}
      />
    </div>
  )
}

export default FailedPostsManager
